// Copyright (c) Zammo Inc.

import { IComboBox, IComboBoxOption, ComboBox, ISelectableOption } from '@fluentui/react';
import React, { memo, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Voice } from '../../app/actions';

import localeMappings, { LocaleMapping } from './Intl';
import { FlagImage } from './styles';

const Title = styled.div`
  text-align: center;
  margin: 10px 0;
`;

export const getFlagImage = (locale?: string) => {
  return locale ? `https://cdn.zammo.ai/public/flags/${locale.toLowerCase()}.svg` : '';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onRenderOption = (item?: ISelectableOption<any>) => (
  <>
    <FlagImage src={getFlagImage(item?.key?.toString())} alt={`${item?.key?.toString()} flag`}></FlagImage>
    <span>{item?.text}</span>
  </>
);

interface Props {
  selectedVoice: string;
  allVoices: Voice[];
  setSelectedVoice: (value: string) => void;
}

const VoicePicker = ({ selectedVoice, allVoices, setSelectedVoice }: Props) => {
  const [filteredVoices, setFilteredVoices] = useState<IComboBoxOption[]>([]);
  const [allLocales, setAllLocales] = useState<IComboBoxOption[]>([]);
  const [selectedLocale, setSelectedLocale] = useState<string>('en-US');

  useEffect(() => {
    const localeFromAllVoices = allVoices.find((v) => v.Locale === selectedVoice)?.Locale;

    if (localeFromAllVoices) {
      setSelectedLocale(localeFromAllVoices);
    } else {
      const locale = selectedVoice.substring(selectedVoice.lastIndexOf('(') + 1, selectedVoice.lastIndexOf(')'));
      setSelectedLocale(locale.split(',')[0].trim());
    }
  }, [selectedVoice]);

  const onVoiceComboBoxChange = (event: React.FormEvent<IComboBox>, item?: IComboBoxOption): void => {
    if (item?.key) {
      setSelectedVoice(item.key.toString());
    }
  };

  const onLocaleComboBoxChange = (event: React.FormEvent<IComboBox>, item?: IComboBoxOption): void => {
    if (item?.key) {
      setSelectedLocale(item.key.toString());
    }
  };

  useEffect(() => {
    const mappedLocaleVoices = allVoices
      .filter((voice: Voice) => voice.Locale == selectedLocale)
      .map((voice: Voice) => ({
        text: `${voice.DisplayName} (${voice.Gender})`,
        key: voice.Name,
      }));

    setFilteredVoices(mappedLocaleVoices);
  }, [allVoices, selectedLocale]);

  useEffect(() => {
    const mappedLocales = [...allVoices.map((item: Voice) => item.Locale)]
      .filter((v: string, i: number, a: string[]) => a.indexOf(v) == i)
      .map((locale: string) => ({
        key: locale,
        text: localeMappings.find((lm: LocaleMapping) => lm.locale == locale)?.display || locale,
      }));

    setAllLocales(mappedLocales);
  }, [allVoices]);

  return (
    <div>
      <Title>Language</Title>
      <ComboBox
        allowFreeform={true}
        autoComplete="on"
        options={allLocales}
        onChange={onLocaleComboBoxChange}
        onRenderOption={onRenderOption}
        selectedKey={selectedLocale}
        style={{ margin: '6px auto', maxWidth: '300px' }}
      ></ComboBox>
      <Title>Voice</Title>
      <ComboBox
        allowFreeform={true}
        autoComplete="on"
        options={filteredVoices}
        onChange={onVoiceComboBoxChange}
        selectedKey={selectedVoice}
        style={{ margin: '6px auto', maxWidth: '300px' }}
      ></ComboBox>
    </div>
  );
};

export default memo(VoicePicker);
